import { LockKeyhole, Search, SlidersHorizontal } from '../components/icons';
import { useMemo, useState } from 'react';
import type { IntegrationStatus } from '../data/financeAdapter';
import { formatCurrency, formatPercent } from '../domain/format';
import type { Assumption, AssumptionType } from '../domain/types';
import { useScenario } from '../state/ScenarioContext';

const typeLabels: Record<AssumptionType, string> = {
  actual: 'Actual',
  forecast: 'Forecast',
  scenario: 'Scenario',
  accounting: 'Accounting',
  policy: 'Policy',
};

export const AssumptionsView = ({
  onNavigate,
  decisionReadiness,
}: {
  onNavigate: (view: 'expenses' | 'revenue' | 'cash') => void;
  decisionReadiness?: IntegrationStatus['decisionReadiness'];
}) => {
  const { data, inputs } = useScenario();
  const [query, setQuery] = useState('');
  const [type, setType] = useState<'all' | AssumptionType>('all');
  const register = useMemo<Assumption[]>(() => [
    ...data.assumptions,
    ...data.revenueLines.map((line) => ({
      id: `line-${line.id}`,
      name: line.name,
      description: `${line.category} monthly revenue forecast.`,
      value: 'Monthly schedule',
      unit: 'text' as const,
      type: 'forecast' as const,
      source: line.source,
      sourceDetail: line.sourceDetail,
      confidence: line.confidence,
      editable: true,
      relatedId: line.id,
    })),
    ...data.expenseLines.filter((line) => line.modelRole !== 'financing_schedule').map((line) => ({
      id: `line-${line.id}`,
      name: line.name,
      description: `${line.category} monthly expense forecast.`,
      value: 'Monthly schedule',
      unit: 'text' as const,
      type: 'forecast' as const,
      source: line.source,
      sourceDetail: line.sourceDetail,
      confidence: line.confidence,
      editable: true,
      relatedId: line.id,
    })),
  ], [data]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return register.filter((item) => {
      const matchesType = type === 'all' || item.type === type;
      const matchesQuery = !needle || `${item.name} ${item.description} ${item.source}`.toLowerCase().includes(needle);
      return matchesType && matchesQuery;
    });
  }, [query, register, type]);

  const displayValue = (assumption: Assumption) => {
    let value = assumption.value;
    if (assumption.relatedId === 'ownerDrawMonthly') value = inputs.ownerDrawMonthly;
    if (assumption.relatedId === 'arRecoveryPercent') value = inputs.arRecoveryPercent / 100;
    if (assumption.unit === 'currency' && typeof value === 'number') return formatCurrency(value);
    if (assumption.unit === 'percent' && typeof value === 'number') return formatPercent(value);
    if (assumption.unit === 'days' && typeof value === 'number') return `${value} days`;
    if (assumption.unit === 'months' && typeof value === 'number') return `${value} months`;
    return String(value);
  };

  const editDestination = (assumption: Assumption) => {
    if (assumption.relatedId === 'ownerDrawMonthly' || assumption.relatedId === 'arRecoveryPercent') return 'cash';
    if (data.expenseLines.some((line) => line.id === assumption.relatedId)) return 'expenses';
    return 'revenue';
  };
  const controls = decisionReadiness?.controls;
  const mappingExceptions = controls?.accountMapping.materialUnmappedAccounts ?? [];

  return (
    <div className="page-stack">
      <section className="page-heading compact">
        <div>
          <p className="eyebrow">Traceable inputs</p>
          <h2>See what the model assumes—and where it came from.</h2>
          <p>Confidence reflects source quality, not whether the assumption will occur. Locked rows protect actuals and model policies.</p>
        </div>
      </section>
      <section className="assumption-summary">
        {(['high', 'medium', 'low'] as const).map((confidence) => (
          <div key={confidence}><span className={`confidence-dot ${confidence}`} /><strong>{register.filter((item) => item.confidence === confidence).length}</strong><span>{confidence} confidence</span></div>
        ))}
        <div><LockKeyhole size={16} /><strong>{register.filter((item) => !item.editable).length}</strong><span>locked controls</span></div>
      </section>
      {controls ? (
        <section className="panel reconciliation-panel">
          <div className="reconciliation-heading">
            <div>
              <p className="eyebrow">Monthly close controls</p>
              <h3>Know which numbers tie—and which still need a decision.</h3>
              <p className="panel-copy">A passed control means the model can reproduce the underlying schedule within the stated tolerance. Mapping exceptions remain separate from forecast lines until their accounting treatment is approved.</p>
            </div>
            <span className={`control-status ${decisionReadiness?.status === 'ready' ? 'passed' : 'review'}`}>
              {decisionReadiness?.status === 'ready' ? 'Decision ready' : 'Review required'}
            </span>
          </div>
          <div className="control-card-grid">
            <article>
              <span>Payroll roster tie-out</span>
              <strong>{controls.payroll.status === 'passed' ? 'Passed' : controls.payroll.status.replace('_', ' ')}</strong>
              <small>{controls.payroll.testedMonths} forecast months tested · max variance {formatCurrency(controls.payroll.maximumAbsoluteDifference)}</small>
            </article>
            <article>
              <span>Approved account mappings</span>
              <strong>{controls.accountMapping.approvedMappingCount}</strong>
              <small>Explicit QuickBooks-to-workbook rules with documented evidence</small>
            </article>
            <article>
              <span>Material mapping exceptions</span>
              <strong>{controls.accountMapping.materialExceptionCount}</strong>
              <small>{formatCurrency(controls.accountMapping.materialExceptionActualTotal)} of actual activity awaiting approval</small>
            </article>
            <article>
              <span>Contractor transaction allocation</span>
              <strong>{controls.accountMapping.professionalContractorsAllocation?.pendingCount === 0 ? 'Passed' : 'Refresh required'}</strong>
              <small>
                {controls.accountMapping.professionalContractorsAllocation
                  ? `${controls.accountMapping.professionalContractorsAllocation.transactionCount} transactions tied monthly · policy ${controls.accountMapping.appliedPolicyVersion ?? 'unknown'}`
                  : `No current allocation control · policy ${controls.accountMapping.policyVersion ?? 'unknown'}`}
              </small>
            </article>
          </div>
          {mappingExceptions.length ? (
            <div className="responsive-table mapping-control-table">
              <table>
                <thead><tr><th>QuickBooks account</th><th>Type</th><th>Actual activity</th><th>Candidate forecast line(s)</th><th>Why it is blocked</th></tr></thead>
                <tbody>
                  {mappingExceptions.map((account) => (
                    <tr key={`${account.side}-${account.id}`}>
                      <td><strong>{account.name}</strong><small>Account {account.accountId ?? account.id}</small></td>
                      <td><span className={`type-badge ${account.side === 'revenue' ? 'actual' : 'accounting'}`}>{account.side}</span></td>
                      <td><strong>{formatCurrency(account.actualTotal)}</strong></td>
                      <td><small>{account.candidateTargetIds.length ? account.candidateTargetIds.join(', ') : 'No candidate documented'}</small></td>
                      <td><small>{account.reviewReason}</small></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="control-pass-note">All QuickBooks accounts above the {formatCurrency(controls.accountMapping.threshold)} materiality threshold have an approved forecast mapping.</p>
          )}
        </section>
      ) : null}
      <section className="panel table-panel">
        <div className="toolbar">
          <label className="search-field"><Search size={17} /><span className="sr-only">Search assumptions</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search assumption, source, or line…" /></label>
          <label className="select-field"><span className="sr-only">Filter assumption type</span><select value={type} onChange={(event) => setType(event.target.value as 'all' | AssumptionType)}><option value="all">All assumption types</option>{Object.entries(typeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <span className="result-count">{filtered.length} assumptions</span>
        </div>
        <div className="responsive-table assumptions-table">
          <table>
            <thead><tr><th>Assumption</th><th>Type</th><th>Value</th><th>Source &amp; rationale</th><th>Confidence</th><th>Control</th></tr></thead>
            <tbody>
              {filtered.map((assumption) => (
                <tr key={assumption.id}>
                  <td><strong>{assumption.name}</strong><small>{assumption.description}</small></td>
                  <td><span className={`type-badge ${assumption.type}`}>{typeLabels[assumption.type]}</span></td>
                  <td><strong>{displayValue(assumption)}</strong></td>
                  <td><strong>{assumption.source}</strong><small>{assumption.sourceDetail}</small></td>
                  <td><span className={`confidence-badge ${assumption.confidence}`}><span />{assumption.confidence}</span></td>
                  <td>{assumption.editable ? <button className="button compact" onClick={() => onNavigate(editDestination(assumption))}><SlidersHorizontal size={14} />Edit</button> : <span className="locked-label"><LockKeyhole size={14} />Locked</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel policy-note">
        <LockKeyhole size={19} />
        <div><strong>Model safeguard</strong><p>Scenario edits affect forecast periods only. Historical actuals remain unchanged, and every active edit is visible in the appropriate operating view.</p></div>
        <button className="button ghost" onClick={() => onNavigate('cash')}>Open cash controls</button>
      </section>
    </div>
  );
};
